<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
    import { CmnhInput } from "@cmnhospitals/components-vue";
    import { CmnhDatepicker } from "@cmnhospitals/components-vue/datepicker";
    import { DateTime } from "luxon";
    import { updateStartDate, updateEndDate } from "@/composables/utils";
    import { sanitizeMoney } from "@/utils";
    import { applyInitialFilters } from "@/composables/search-utils";
    import StateSelect from "@/components/common/StateSelect.vue";

    const props = defineProps<{
        initialFilters?: Record<string, string>;
    }>();

    const emit = defineEmits(["update-search", "reset-search"]);

    const today = DateTime.now().toISO(); // Ensure today is an ISO 8601 string
    const startDatepickerKey = ref(0);
    const endDatepickerKey = ref(0);

    const defaultFilters = {
        locationName: "",
        locationCity: "",
        locationState: "", // Empty string shows placeholder in CmnhDropdown
        locationNumber: "",
        associateName: "",
        employeeNumber: "",
        offlineTransactionNumber: "",
        amount: "",
        checkNumber: "",
        startDate: undefined as string | undefined,
        endDate: undefined as string | undefined,
    };

    const searchFilters = ref(applyInitialFilters(defaultFilters, props.initialFilters));

    // Accept optional date parameter to handle cleared dates from datepicker
    function setStartDate(date?: Date | Date[]) {
        // Only call updateStartDate if date is defined, otherwise clear the date
        if (!date) {
            searchFilters.value.startDate = undefined;
            return;
        }
        updateStartDate(date, searchFilters.value);
    }

    // Accept optional date parameter to handle cleared dates from datepicker
    function setEndDate(date?: Date | Date[]) {
        // Only call updateEndDate if date is defined, otherwise clear the date
        if (!date) {
            searchFilters.value.endDate = undefined;
            return;
        }
        updateEndDate(date, searchFilters.value);
    }

    const datepickerKey = ref(0);

    function clearFilters() {
        // Reset all filters to default values
        searchFilters.value = {
            ...defaultFilters,
            startDate: undefined,
            endDate: undefined,
        };

        // Force a re-render by changing the key
        datepickerKey.value++;

        // Manually clear any lingering values in the input fields
        setTimeout(() => {
            document.querySelectorAll(".air-datepicker__input").forEach(input => {
                (input as HTMLInputElement).value = "";
            });
        }, 50);

        // ✅ Emit the new event to reset the grid to its default state.
        // The cleared shape goes with it so the caller can drop these keys from the URL.
        emit("reset-search", searchFilters.value);
    }

    // Strip "$", "#", commas, etc. from the amount so the cleaned value is shown
    // in the field and stored in the query params. Runs on blur and before search.
    // Pure non-numeric junk (e.g. "detroit") has no digits to keep, so leave it as
    // typed — it then flows through as a non-matching filter and shows "no results",
    // consistent with the other search fields rather than silently clearing.
    function normalizeAmount() {
      const cleaned = sanitizeMoney(searchFilters.value.amount);
      searchFilters.value.amount = cleaned || searchFilters.value.amount;
    }

    function onSearch() {
      normalizeAmount();

      const hasFilters = Object.values(searchFilters.value).some(
          val => val !== undefined && val !== null && val !== ''
      );

      if (hasFilters) {
        emit("update-search", searchFilters.value);
      } else {
        emit("reset-search", searchFilters.value);
      }
    }

    watch(() => searchFilters.value.startDate, () => {
        endDatepickerKey.value++; // ✅ Force re-render To Date when From Date changes
    });

    watch(() => searchFilters.value.endDate, () => {
        startDatepickerKey.value++; // ✅ Force re-render From Date when To Date changes
    });

    // Listen for Enter key on any input and trigger search
    onMounted(() => {
      const inputEls = document.querySelectorAll(".search-form input");

      inputEls.forEach(input => {
        input.addEventListener("keydown", ((e: Event) => {
          const ke = e as KeyboardEvent;
          if (ke.key === "Enter") {
            ke.preventDefault();
            normalizeAmount();
            emit("update-search", searchFilters.value);
          }
        }) as EventListener);
      });
    });

</script>

<template>
  <form @submit.prevent="onSearch">
    <div class="search-form">
        <h2>Refine Search Criteria</h2>
        <div class="form-group">
            <CmnhInput type="search" label="Office Name" placeholder="Office Name" :hide-label="true" v-model="searchFilters.locationName" />
            <CmnhInput type="search" label="Office City" placeholder="Office City" :hide-label="true" v-model="searchFilters.locationCity" />
            <StateSelect v-model="searchFilters.locationState" :hide-label="true" />
            <CmnhInput type="search" label="Office ID" placeholder="Office ID" :hide-label="true" v-model="searchFilters.locationNumber" />
        </div>
        <div class="form-group">
            <CmnhInput type="search" label="Associate Name" placeholder="Associate Name" :hide-label="true" v-model="searchFilters.associateName" />
            <CmnhInput type="search" label="Associate ID" placeholder="Associate ID" :hide-label="true" v-model="searchFilters.employeeNumber" />
            <CmnhInput type="search" label="Payment #" placeholder="Payment #" :hide-label="true" v-model="searchFilters.offlineTransactionNumber" />
            <CmnhInput type="search" label="Payment Amount" placeholder="Payment Amount" :hide-label="true" v-model="searchFilters.amount" @blur="normalizeAmount" />
        </div>
        <div class="form-group">
            <CmnhInput type="search" label="Check Number" placeholder="Check Number" :hide-label="true" v-model="searchFilters.checkNumber" />
            <div class="search-form--dates">
                <div>
                    <CmnhDatepicker :key="startDatepickerKey" label="From" :hide-label="true" :hideInitialValue="true" @date-selected="setStartDate" :selectedDate="searchFilters.startDate || undefined" :max-date="searchFilters.endDate || today" :auto-close="true" placeholder="From Date" />
                </div>
                <div>
                    <CmnhDatepicker :key="endDatepickerKey" label="To" :hide-label="true" :hideInitialValue="true" @date-selected="setEndDate" :selectedDate="searchFilters.endDate || undefined" :max-date="today" :min-date="searchFilters.startDate" :auto-close="true" placeholder="To Date" position="bottom right" />
                </div>
            </div>
        </div>
      
        <div class="form-group action-btns-container">
          <button @click="clearFilters" class="btn btn-default" data-qa-id="receive-payments-clear-button">Clear</button>
          <button type="submit" class="btn btn-primary" data-qa-id="receive-payments-search-button">Search</button>
        </div>
    </div>
  </form>
</template>

<style scoped lang="scss">
@import "../../../Styles/imports";

.search-form {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: var(--cmnh-size-spacing-6);
    background-color: var(--cmnh-color-gray-200);
    padding: 0 var(--cmnh-size-spacing-6);

    h2 {
        color: var(--cmnh-color-dark);
        font-weight: 600;
        margin: var(--cmnh-size-spacing-6) 0 0;
        font-size: 14px;
        width: 100%;
    }

    // .cmnh-label, .cmnh-input__icon-clear, .cmnh-input__icon--left are global in cmnh-components.scss

    // Input element inside CmnhInput child component — requires :deep()
    :deep(.cmnh-input) {
        font-size: 11px;
        font-weight: 600;
        box-shadow: none;
        padding: 6px 12px;
        line-height: 1.53;
        color: var(--cmnh-color-gray-600);
        background-color: var(--cmnh-color-white);
        background-image: none;
        border: 1px solid var(--cmnh-color-gray-200);
        border-radius: 0;
        box-shadow: inset 0 1px 1px color-mix(in srgb, var(--cmnh-color-black) 7.5%, transparent);
        transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;

        &:focus {
            border-color: var(--cmnh-color-light-blue-300);
            outline: 0;
            box-shadow: inset 0 1px 1px color-mix(in srgb, var(--cmnh-color-black) 7.5%, transparent), 0 0 8px color-mix(in srgb, var(--cmnh-color-light-blue-300) 60%, transparent);
        }
    }

    .search-form--dates {
        display: flex;
        gap: var(--cmnh-size-spacing-6);
        align-items: center;

        & > div {
            width: 50%;

            @media (max-width: $screen-sm-max) {
                width: 100%;
            }
        }
    }

    .search-form--dates-clear {
        display: flex;
        justify-content: flex-end;
    }

    select.form-control {
        padding-inline-start: 8px;
    }

    // Select label + select element inside CmnhDropdown child component — requires :deep()
    :deep(.cmnh-select-label) {
        display: none;
        visibility: hidden;
    }

    :deep(.cmnh-select) {
        display: block;
        width: 100%;
        height: 31px;
        padding: 6px 12px;
        padding-inline-start: 8px;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.428571429;
        color: var(--cmnh-color-gray-600);
        background-color: var(--cmnh-color-white);
        background-image: none;
        border: 1px solid var(--cmnh-color-gray-100);
        border-radius: 0;
        box-shadow: inset 0 1px 1px color-mix(in srgb, var(--cmnh-color-black) 7.5%, transparent);
        transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
    }

    .form-group {
        margin-bottom: 0;
    }

    .form-group:not(.action-btns-container) {
        width: 32%;
        display: flex;
        flex-direction: column;
        gap: var(--cmnh-size-spacing-6);

        @media (max-width: $screen-sm-max) {
            width: 48%;
            margin-bottom: 0;
        }

        @media (max-width: $screen-xs-max) {
            width: 100%;
            margin-bottom: 0;
        }
    }

    .action-btns-container {
        display: flex;
        justify-content: flex-end;
        margin-bottom: var(--cmnh-size-spacing-6);
        width: 100%;

        button:first-child {
            margin-right: var(--cmnh-size-spacing-6);
        }
    }
}
</style>
